import { useEffect } from 'react';

/**
 * Hook for mobile touch optimizations
 */
export function useTouchOptimizations() {
  useEffect(() => {
    // Prevent double-tap zoom on buttons/interactive elements
    let lastTouchEnd = 0;
    const preventDoubleTapZoom = (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };

    document.addEventListener('touchend', preventDoubleTapZoom, false);

    return () => {
      document.removeEventListener('touchend', preventDoubleTapZoom);
    };
  }, []);
}

/**
 * Hook for swipe gestures
 * @param {Function} onSwipeLeft - Callback when swiped left
 * @param {Function} onSwipeRight - Callback when swiped right
 * @param {number} threshold - Minimum distance in pixels to trigger swipe (default: 50)
 * @param {HTMLElement} element - Element to attach listeners to (default: document)
 */
export function useSwipeGesture(onSwipeLeft, onSwipeRight, threshold = 50, element = null) {
  useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;

    const targetElement = element || document;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      touchEndY = e.changedTouches[0].screenY;
      handleSwipe();
    };

    const handleSwipe = () => {
      const diffX = touchStartX - touchEndX;
      const diffY = touchStartY - touchEndY;

      // Only trigger if horizontal swipe is more prominent than vertical
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > threshold) {
        if (diffX > 0) {
          // Swiped left
          onSwipeLeft?.();
        } else {
          // Swiped right
          onSwipeRight?.();
        }
      }
    };

    targetElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    targetElement.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      targetElement.removeEventListener('touchstart', handleTouchStart);
      targetElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onSwipeLeft, onSwipeRight, threshold, element]);
}

/**
 * Hook for pull-to-refresh functionality
 * @param {Function} onRefresh - Callback when pull-to-refresh is triggered
 * @param {HTMLElement} scrollElement - Scrollable element (default: window)
 */
export function usePullToRefresh(onRefresh, scrollElement = null) {
  useEffect(() => {
    const target = scrollElement || window;
    let startY = 0;
    let currentY = 0;
    let pullDistance = 0;

    const handleTouchStart = (e) => {
      // Only enable if scrolled to top
      const scrollTop = scrollElement?.scrollTop || window.scrollY;
      if (scrollTop === 0) {
        startY = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e) => {
      if (startY === 0) return;
      
      const scrollTop = scrollElement?.scrollTop || window.scrollY;
      if (scrollTop > 0) {
        startY = 0;
        return;
      }

      currentY = e.touches[0].clientY;
      pullDistance = currentY - startY;

      // Only pull down, not up
      if (pullDistance > 0 && scrollTop === 0) {
        // Prevent default scroll when pulling
        if (pullDistance > 10) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = async () => {
      if (pullDistance > 60) { // Threshold to trigger refresh
        await onRefresh?.();
      }
      
      pullDistance = 0;
      startY = 0;
    };

    if (scrollElement) {
      scrollElement.addEventListener('touchstart', handleTouchStart, { passive: true });
      scrollElement.addEventListener('touchmove', handleTouchMove, { passive: false });
      scrollElement.addEventListener('touchend', handleTouchEnd);
    } else {
      document.addEventListener('touchstart', handleTouchStart, { passive: true });
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('touchstart', handleTouchStart);
        scrollElement.removeEventListener('touchmove', handleTouchMove);
        scrollElement.removeEventListener('touchend', handleTouchEnd);
      } else {
        document.removeEventListener('touchstart', handleTouchStart);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [onRefresh, scrollElement]);
}

